import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap-v5';
import clsx from 'clsx';
import appMoment from 'src/helpers/app-moment';
import formatDurationString from './utils/format-duration-string';

/**
 * Geri sayım (countdown) için kullanılabilecek tick interval değerleri.
 * Component'in ne sıklıkla güncelleneceğini belirler.
 *
 * @enum {number}
 */
export enum AppCountDownTick {
  /** Her saniye güncelleme (1000ms) */
  SECONDS = 1_000,
  /** Her dakika güncelleme (60000ms) */
  MINUTES = AppCountDownTick.SECONDS * 60,
  /** Her saat güncelleme (3600000ms) */
  HOURS = AppCountDownTick.MINUTES * 60,
}

/**
 * Kalan süre hakkında yardımcı bilgiler sağlar.
 * Format fonksiyonlarında ve tick hesaplamalarında kullanılabilir.
 *
 * @interface
 */
export interface IRemainingTimeHelpers {
  /** Kalan sürenin belirli birimlerden az olup olmadığını kontrol eder */
  lessThan: {
    /** Kalan süre 1 günden az mı? */
    day: boolean;
    /** Kalan süre 1 saatten az mı? (ve 1 günden az) */
    hour: boolean;
    /** Kalan süre 1 dakikadan az mı? (ve 1 saatten az) */
    minute: boolean;
    /** Kalan süre 1 saniyeden az mı? (ve 1 dakikadan az) */
    second: boolean;
  };
}

/**
 * Format fonksiyonuna ve tick fonksiyonuna geçirilen parametreler.
 *
 * @interface
 */
export interface IFormatOptions {
  /** Kalan süre (milisaniye cinsinden) */
  remainingTimeMs: number;
  /** Kalan süre hakkında yardımcı bilgiler */
  helpers: IRemainingTimeHelpers;
}

/**
 * AppCountDown component'inin prop'ları.
 *
 * @interface IProps
 */
interface IProps {
  /** Bitiş tarihi. Date objesi, timestamp (number) veya ISO string formatında olabilir. */
  endDate: Date | number | string | appMoment.Moment;
  /**
   * Süre formatı. String veya dinamik format fonksiyonu olabilir.
   *
   * String formatı için `formatDurationString` fonksiyonunun dokümantasyonuna bakın.
   * Özel token'lar: `[DD]` (gün), `[HH]` (saat), `[mm]` (dakika), `[ss]` (saniye)
   * Koşullu bloklar: `{[DD] gün}` - değer 0 ise gösterilmez
   *
   * Fonksiyon formatı: `(opts: IFormatOptions) => string`
   * - `opts.remainingTimeMs`: Kalan süre (ms)
   * - `opts.helpers`: Yardımcı bilgiler (lessThan.day, lessThan.hour, vb.)
   *
   * @default '{[DD] gün} {[HH] saat} {[mm] dakika} {[ss] saniye}'
   */
  format?: string | ((opts: IFormatOptions) => string);
  /**
   * Timer'ın ne sıklıkla güncelleneceğini belirler.
   *
   * - `false`: Otomatik güncelleme yok (sadece ilk render'da hesaplanır)
   * - `AppCountDownTick`: Önceden tanımlı interval değerleri (SECONDS, MINUTES, HOURS)
   * - `number`: Özel interval değeri (milisaniye cinsinden)
   * - `(opts: IFormatOptions) => AppCountDownTick | number | false`: Dinamik interval
   *
   * Dinamik interval örneği: Kalan süre 1 saatten azsa her saniye, değilse her dakika güncelle
   *
   * @default false
   */
  tick?: false | AppCountDownTick | ((opts: IFormatOptions) => AppCountDownTick | number | false);
  /**
   * Süre dolduğunda gösterilecek metin.
   *
   * @default 'Süresi doldu'
   */
  expiredText?: string;
  /**
   * Süre dolduğunda gösterilecek metin için ek CSS class'ları.
   *
   * @default ''
   */
  expiredTextClassName?: string;
  /**
   * Tooltip gösterilip gösterilmeyeceği.
   * Tooltip, bitiş tarihinin tam formatını gösterir.
   *
   * @default true
   */
  tooltip?: boolean;
  /**
   * Tooltip'te gösterilecek tarih formatı.
   * Moment.js format string'i kullanılır.
   *
   * @default 'D MMMM YYYY, dddd - HH:mm:ss'
   */
  tooltipFormat?: string;
  /**
   * Component'e eklenecek ek CSS class'ları.
   *
   * @default ''
   */
  className?: string;
  /**
   * Süre bittiğinde tetiklenecek callback fonksiyonu.
   */
  onExpire?: () => void;
}

/**
 * Varsayılan format string'i.
 * Koşullu bloklar kullanarak sadece 0 olmayan değerleri gösterir.
 */
const DEFAULT_FORMAT = '{[DD] gün} {[HH] saat} {[mm] dakika} {[ss] saniye}';

/**
 * Geri sayım (countdown) component'i.
 *
 * Belirli bir bitiş tarihine kadar kalan süreyi gösterir ve belirli aralıklarla güncellenir.
 * Süre dolduğunda belirtilen metni gösterir. İsteğe bağlı olarak tooltip ile tam tarih bilgisi
 * gösterilebilir.
 *
 * @component
 * @param {IProps} props - Component prop'ları
 * @param {Date | number | string} props.endDate - Bitiş tarihi. Date objesi, timestamp (number) veya ISO string formatında olabilir.
 * @param {string | ((opts: IFormatOptions) => string)} [props.format] - Süre formatı. String veya dinamik format fonksiyonu. Varsayılan: `'{[DD] gün} {[HH] saat} {[mm] dakika} {[ss] saniye}'`
 * @param {false | AppCountDownTick | ((opts: IFormatOptions) => AppCountDownTick | number | false)} [props.tick] - Timer'ın ne sıklıkla güncelleneceği. Varsayılan: `false`
 * @param {string} [props.expiredText] - Süre dolduğunda gösterilecek metin. Varsayılan: `'Süresi doldu'`
 * @param {boolean} [props.tooltip] - Tooltip gösterilip gösterilmeyeceği. Varsayılan: `true`
 * @param {string} [props.tooltipFormat] - Tooltip'te gösterilecek tarih formatı (Moment.js format string'i). Varsayılan: `'D MMMM YYYY, dddd - HH:mm:ss'`
 * @param {string} [props.className] - Component'e eklenecek ek CSS class'ları. Varsayılan: `''`
 *
 * @example
 * // Basit kullanım - varsayılan format ve ayarlarla
 * <AppCountDown endDate={new Date('2024-12-31T23:59:59')} />
 *
 * @example
 * // Özel format string ile
 * <AppCountDown
 *   endDate="2024-12-31T23:59:59"
 *   format="{[DD] gün} {[HH] saat} {[mm] dakika}"
 * />
 *
 * @example
 * // Dinamik format fonksiyonu ile
 * <AppCountDown
 *   endDate={endDate}
 *   format={({ remainingTimeMs, helpers }) => {
 *     if (helpers.lessThan.hour) {
 *       return `{[mm] dakika} {[ss] saniye}`;
 *     }
 *     return `{[DD] gün} {[HH] saat}`;
 *   }}
 * />
 *
 * @example
 * // Her saniye güncelleme ile
 * <AppCountDown
 *   endDate={endDate}
 *   tick={AppCountDownTick.SECONDS}
 * />
 *
 * @example
 * // Dinamik tick interval - 1 saatten azsa her saniye, değilse her dakika
 * <AppCountDown
 *   endDate={endDate}
 *   tick={({ helpers }) => {
 *     return helpers.lessThan.hour
 *       ? AppCountDownTick.SECONDS
 *       : AppCountDownTick.MINUTES;
 *   }}
 * />
 *
 * @example
 * // Tooltip olmadan ve özel expired text ile
 * <AppCountDown
 *   endDate={endDate}
 *   tooltip={false}
 *   expiredText="Kampanya sona erdi"
 * />
 *
 * @example
 * // Özel tooltip formatı ile
 * <AppCountDown
 *   endDate={endDate}
 *   tooltipFormat="DD.MM.YYYY HH:mm"
 * />
 */
function AppCountDown(props: IProps) {
  const {
    endDate,
    format,
    tick = false,
    expiredText = 'Süresi doldu',
    expiredTextClassName = '',
    tooltip = true,
    tooltipFormat = 'D MMMM YYYY, dddd - HH:mm:ss',
    className = '',
    onExpire,
  } = props;

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const onExpireRef = useRef(onExpire);
  const hasExpiredTriggeredRef = useRef(false);

  const [timerTickInterval, setTimerTickInterval] = useState(0);

  const [remainingTimeMs, setRemainingTimeMs] = useState(0);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  const endDateMs = useMemo(() => {
    let endDateMs = 0;

    if (typeof endDate === 'string') {
      endDateMs = appMoment(endDate).toDate().getTime();
    } else if (typeof endDate === 'number') {
      endDateMs = endDate;
    } else if (endDate instanceof Date) {
      endDateMs = endDate.getTime();
    } else if (appMoment.isMoment(endDate)) {
      endDateMs = endDate.toDate().getTime();
    }

    return endDateMs;
  }, [endDate]);

  useEffect(() => {
    hasExpiredTriggeredRef.current = false;
  }, [endDateMs]);

  const calculateAndGetRemainingTime = useCallback(() => {
    const now = Date.now();
    const newRemainingTime = Math.max(0, endDateMs - now);

    return newRemainingTime;
  }, [endDateMs]);

  const getRemainingTimeHelpers = useCallback(() => {
    const remainingTimeMs = calculateAndGetRemainingTime();
    const duration = appMoment.duration(remainingTimeMs, 'milliseconds');

    const lessThan = {
      get day() {
        return duration.asDays() < 1;
      },
      get hour() {
        return duration.hours() < 1 && this.day === true;
      },
      get minute() {
        return duration.minutes() < 1 && this.hour === true;
      },
      get second() {
        return duration.seconds() < 1 && this.minute === true;
      },
    };

    return {
      lessThan,
    };
  }, [calculateAndGetRemainingTime]);

  const fullDateTitle = useMemo(() => {
    return appMoment(endDateMs).format(tooltipFormat);
  }, [endDateMs, tooltipFormat]);

  const isExpired = useMemo(() => {
    return remainingTimeMs <= 0;
  }, [remainingTimeMs]);

  const remainingTimeStr = useMemo(() => {
    if (isExpired) return expiredText;

    const duration = appMoment.duration(remainingTimeMs, 'milliseconds');

    const formatStr = typeof format === 'function' ? format({ remainingTimeMs: remainingTimeMs, helpers: getRemainingTimeHelpers() }) : format;

    return formatDurationString(duration, formatStr ?? DEFAULT_FORMAT);
  }, [remainingTimeMs, isExpired, expiredText, format, getRemainingTimeHelpers]);

  const tooltipId = useMemo(() => {
    return `app-count-down-${Math.random().toString(36).substring(2, 15)}`;
  }, []);

  const Content = useMemo(() => {
    return (
      <time
        dateTime={new Date(endDateMs).toISOString()}
        className={clsx('w-fit _dtt', className, {
          [expiredTextClassName || '']: isExpired,
        })}
      >
        {remainingTimeStr}
      </time>
    );
  }, [endDateMs, remainingTimeStr, className, isExpired, expiredTextClassName]);

  useEffect(() => {
    const cleanup = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };

    if (!timerTickInterval) return cleanup;

    timerRef.current = setInterval(() => {
      const newRemainingTimeMs = calculateAndGetRemainingTime();
      setRemainingTimeMs(newRemainingTimeMs);

      if (newRemainingTimeMs <= 0) {
        cleanup();
        setTimerTickInterval(0);

        // Süre sıfırlandığında sadece bir kez tetikle
        if (!hasExpiredTriggeredRef.current) {
          hasExpiredTriggeredRef.current = true;
          onExpireRef.current?.();
        }
      }
    }, timerTickInterval);

    return cleanup;
  }, [calculateAndGetRemainingTime, timerTickInterval]);

  useEffect(() => {
    setRemainingTimeMs(calculateAndGetRemainingTime());
  }, [calculateAndGetRemainingTime]);

  useEffect(() => {
    const remainingTimeMs = calculateAndGetRemainingTime();
    if (remainingTimeMs <= 0) {
      setTimerTickInterval(0);
      return;
    }

    const _tick = typeof tick === 'function' ? tick({ remainingTimeMs, helpers: getRemainingTimeHelpers() }) : tick;

    if (_tick === false) {
      setTimerTickInterval(0);
      return;
    }

    if (typeof _tick === 'number') {
      setTimerTickInterval(_tick);
      return;
    }
  }, [tick, calculateAndGetRemainingTime, getRemainingTimeHelpers]);

  if (!tooltip) return Content;

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`dt-${tooltipId}`}>
          <span>{fullDateTitle}</span>
        </Tooltip>
      }
    >
      {Content}
    </OverlayTrigger>
  );
}

export default memo(AppCountDown);
